/**
 * Domain utility functions
 */

/**
 * List of common free webmail domains
 */
export const FREE_EMAIL_DOMAINS = [
    'gmail.com',
    'yahoo.com',
    'hotmail.com',
    'outlook.com',
    'aol.com',
    'icloud.com',
    'protonmail.com',
    'mail.com',
    'zoho.com',
    'yandex.com',
    'gmx.com',
    'live.com',
    'msn.com',
    'me.com',
    'inbox.com',
    'googlemail.com'
];

/**
 * Extracts the domain from an email address
 * @param {string} email - Email address
 * @returns {string|null} - Domain or null if invalid
 */
export function extractDomainFromEmail(email) {
    if (!email || typeof email !== 'string') return null;
    
    try {
        const parts = email.split('@');
        if (parts.length !== 2 || !parts[1]) return null;
        
        return parts[1].toLowerCase().trim();
    } catch (error) {
        console.error('Error extracting domain from email:', error);
        return null;
    }
}

/**
 * Checks if an email domain is a free webmail provider
 * @param {string} domain - Email domain to check
 * @returns {boolean} - True if it's a free email domain
 */
export function isFreeEmailDomain(domain) {
    if (!domain) return false;
    return FREE_EMAIL_DOMAINS.includes(domain.toLowerCase());
}

/**
 * Makes an educated guess for a website URL based on domain
 * @param {string} domain - Email or website domain
 * @returns {string} - Guessed website URL
 */
export function guessWebsiteUrl(domain) {
    if (!domain) return '';
    
    // If it's a free email domain, don't make a guess
    if (isFreeEmailDomain(domain)) return '';
    
    // Otherwise, create a website URL guess
    const normalizedDomain = domain.toLowerCase().trim();
    return `https://www.${normalizedDomain}`;
}

/**
 * Extracts company name from domain (best guess)
 * @param {string} domain - Domain to analyze
 * @returns {string} - Guessed company name
 */
export function guessCompanyName(domain) {
    if (!domain || isFreeEmailDomain(domain)) return '';
    
    try {
        // Remove TLD (.com, .org, etc)
        const parts = domain.split('.');
        
        if (parts.length >= 2) {
            // Get the main domain part (usually the second-to-last)
            const mainPart = parts[parts.length - 2];
            
            // Format it nicely (capitalize first letter of each word)
            return mainPart
                .split(/[-_]/)
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        }
        
        return '';
    } catch (error) {
        console.error('Error guessing company name:', error);
        return '';
    }
} 